import {
  BankOutlined,
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  StarFilled,
} from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Descriptions,
  Form,
  Input,
  Popconfirm,
  Popover,
  Row,
  Select,
  Space,
  Tag,
  Tooltip,
  message,
} from "antd";
import { COUNTRIES, DATE_TIME_FORMAT, DEFAULT_COUNTRY } from "common/constants";
import { formatError } from "common/redux/middleware/queryErrorLogger";
import { camelToWords, formatDate, snakeToCamelCase } from "common/utils";
import { AktDatePicker } from "components/aktDatePicker";
import { DisplayComponent } from "components/formItems";
import { useUserType } from "features/auth";
import {
  useCreateDebtorEmploymentsMutation,
  useDeleteDebtorEmploymentMutation,
  useReplicatePacketedDebtorEmploymentsMutation,
  useUpdateDebtorEmploymentsMutation,
} from "features/debtorSider/agencyPortal/debtorSiderAPI";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import { PERMISSIONS } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import moment from "moment-timezone";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { useCompactMode } from "providers/compactModeProvider";
import styled from "styled-components";

const StyledCard = styled.div`
  margin-top: 0px;
  margin-left: 8px;
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;

  h4 {
    margin: 2px 0;
  }
`;

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyledTag = styled(Tag)`
  margin-right: 0;
  font-weight: normal;
`;

const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 4px;
  }
`;

const StyledCompactRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 0;
  min-width: 0;

  .ant-btn-link {
    padding: 0;
    min-width: auto;
    height: auto;
    line-height: 1;
  }
`;

const StyledCompactPrimary = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledStarFilled = styled(StarFilled)`
  color: #faad14;
  flex-shrink: 0;
`;

const StyledTrailingActions = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const StyledLeadingIcon = styled(BankOutlined)`
  color: #8c8c8c;
  flex-shrink: 0;
`;

const StyledInfoIcon = styled(InfoCircleOutlined)`
  color: #1677ff;
  cursor: pointer;
`;

const StyledDescriptions = styled(Descriptions)`
  white-space: nowrap;
`;

export function DebtorEmploymentInfoCard({
  triggerReplicatePacketedDebtorUpdatesModal = () => {},
  employment = undefined,
  isNew = undefined,
  onCreate = undefined,
  onCancel = undefined,
  title = undefined,
  isFirstItem = true,
}) {
  const { isAgencyUserType } = useUserType();
  const { isCompactMode } = useCompactMode();
  const [form] = Form.useForm();
  const { debtorId } = useParams();
  const [isEditing, setIsEditing] = useState(isNew || false);
  const [country, setCountry] = useState(employment?.companyAddressCountry || DEFAULT_COUNTRY);
  const { data: constants } = useFetchBackendConstantsQuery();
  const [createDebtorEmployment, { isLoading: isCreateLoading }] =
    useCreateDebtorEmploymentsMutation();
  const [updateDebtorEmployment, { isLoading: isUpdateLoading }] =
    useUpdateDebtorEmploymentsMutation();
  const [deleteDebtorEmployment, { isLoading: isDeleteLoading }] =
    useDeleteDebtorEmploymentMutation();
  const [replicatePacketedDebtorEmployments] = useReplicatePacketedDebtorEmploymentsMutation();

  const {
    data: {
      [PERMISSIONS.DEBTOR_EMPLOYMENT__CREATE]: isAddDebtorEmploymentAuthorized,
      [PERMISSIONS.DEBTOR_EMPLOYMENT__UPDATE]: isUpdateDebtorEmploymentAuthorized,
      [PERMISSIONS.DEBTOR_EMPLOYMENT__DELETE]: isDeleteDebtorEmploymentAuthorized,
    },
  } = useAuthorizations();

  const editableDataSourceTypeValues = constants?.editableDataSourceTypes?.map(
    ({ value }) => value,
  );

  const filterOption = (inputValue, option) => {
    const fullOptionText = option.label;
    return fullOptionText?.toLowerCase().includes(inputValue?.toLowerCase());
  };

  const deleteEmployment = async () => {
    const result = await deleteDebtorEmployment({ debtorId, employmentId: employment.id });
    if ("data" in result) {
      message.success("Employment deleted successfully!");
    }
  };

  const toggleIsEditing = async () => {
    if (isEditing) {
      let values;
      try {
        values = await form.validateFields();
      } catch {
        return null; // Form will handle error messages
      }

      const action = isNew
        ? createDebtorEmployment({
            debtorId,
            ...values,
          })
        : updateDebtorEmployment({
            debtorId,
            employmentId: employment.id,
            ...values,
          });
      const result = await action;

      if ("data" in result) {
        message.success("Saved successfully!");
        if (values.isPrimary) {
          triggerReplicatePacketedDebtorUpdatesModal({
            action: replicatePacketedDebtorEmployments,
            dataId: result.data.id,
            newData: result.data.companyName,
            type: "employment",
          });
        }
        if (isNew) onCreate();
      }

      if ("error" in result) {
        form.setFields(formatError(result.error));
        message.error("Could not update employment");
        return;
      }
      setIsEditing(false);
    } else {
      setIsEditing(true);
      form.setFieldsValue({
        ...employment,
        companyAddressCountry: employment?.companyAddressCountry || DEFAULT_COUNTRY,
      });
      setCountry(employment?.companyAddressCountry || DEFAULT_COUNTRY);
    }
  };

  const onPrimaryCancel = () => {
    setIsEditing(false);
  };

  const formItemLayout = {
    labelCol: {
      xs: { span: 20 },
      sm: { span: 12 },
    },
  };

  return (
    <StyledCard>
      {(isFirstItem || isNew || isEditing || !isCompactMode) && (
        <StyledHeader>
          <h4>
            <Space size="small">
              {isNew ? "New Employment" : title}
              {!isCompactMode && employment?.isPrimary && !isEditing && (
                <StyledTag color="gold">PRIMARY</StyledTag>
              )}
            </Space>
          </h4>
          {!isCompactMode &&
            !isNew &&
            !isEditing &&
            isAgencyUserType &&
            (isUpdateDebtorEmploymentAuthorized || isDeleteDebtorEmploymentAuthorized) && (
              <Row>
                {isUpdateDebtorEmploymentAuthorized && (
                  <Button type="link" icon={<EditOutlined />} onClick={toggleIsEditing} />
                )}
                {isDeleteDebtorEmploymentAuthorized && (
                  <Popconfirm
                    placement="topRight"
                    okText="Yes"
                    title="Are you sure you want to delete this employment?"
                    onConfirm={deleteEmployment}
                  >
                    <Button loading={isDeleteLoading} type="link" icon={<DeleteOutlined />} />
                  </Popconfirm>
                )}
              </Row>
            )}
        </StyledHeader>
      )}

      {!isEditing && isCompactMode && (
        <StyledCompactRow>
          <StyledLeadingIcon />
          <Tooltip title={employment.companyName}>
            <StyledCompactPrimary>{employment.companyName}</StyledCompactPrimary>
          </Tooltip>
          {employment.isPrimary && (
            <Tooltip title="Primary">
              <StyledStarFilled />
            </Tooltip>
          )}
          {employment.employmentType && (
            <StyledTag>{camelToWords(snakeToCamelCase(employment.employmentType))}</StyledTag>
          )}
          <StyledTrailingActions>
            <Popover
              trigger="hover"
              placement="top"
              content={
                <StyledDescriptions size="small" column={1} bordered>
                  {employment.isPrimary ? (
                    <Descriptions.Item label="Primary">Yes</Descriptions.Item>
                  ) : null}
                  {employment.jobTitle ? (
                    <Descriptions.Item label="Job Title">{employment.jobTitle}</Descriptions.Item>
                  ) : null}
                  {employment.salary ? (
                    <Descriptions.Item label="Salary">{employment.salary}</Descriptions.Item>
                  ) : null}
                  {employment.companyAddress1 || employment.companyAddressCity ? (
                    <Descriptions.Item label="Address">
                      {[
                        employment.companyAddress1,
                        employment.companyAddress2,
                        employment.companyAddressCity,
                        employment.companyAddressState,
                        employment.companyAddressZipCode,
                        employment.companyAddressCountry &&
                        employment.companyAddressCountry !== DEFAULT_COUNTRY
                          ? COUNTRIES.find((c) => c.value === employment.companyAddressCountry)
                              ?.label || employment.companyAddressCountry
                          : null,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </Descriptions.Item>
                  ) : null}
                  {employment.companyPhoneNumber ? (
                    <Descriptions.Item label="Phone">
                      {employment.companyPhoneNumber}
                    </Descriptions.Item>
                  ) : null}
                  {employment.startDate ? (
                    <Descriptions.Item label="Start Date">
                      {formatDate(employment.startDate)}
                    </Descriptions.Item>
                  ) : null}
                  {employment.endDate ? (
                    <Descriptions.Item label="End Date">
                      {formatDate(employment.endDate)}
                    </Descriptions.Item>
                  ) : null}
                  {employment.employmentType ? (
                    <Descriptions.Item label="Employment Type">
                      {camelToWords(snakeToCamelCase(employment.employmentType))}
                    </Descriptions.Item>
                  ) : null}
                  {employment.dataSource ? (
                    <Descriptions.Item label="Data Source">
                      {camelToWords(snakeToCamelCase(employment.dataSource))}
                    </Descriptions.Item>
                  ) : null}
                  {employment.createdAt ? (
                    <Descriptions.Item label="Created At">
                      {moment(employment.createdAt).format(DATE_TIME_FORMAT)}
                    </Descriptions.Item>
                  ) : null}
                  {employment.notes ? (
                    <Descriptions.Item label="Notes">{employment.notes}</Descriptions.Item>
                  ) : null}
                </StyledDescriptions>
              }
            >
              <StyledInfoIcon />
            </Popover>
            {isAgencyUserType &&
              (isUpdateDebtorEmploymentAuthorized || isDeleteDebtorEmploymentAuthorized) && (
                <>
                  {isUpdateDebtorEmploymentAuthorized && (
                    <Button type="link" icon={<EditOutlined />} onClick={toggleIsEditing} />
                  )}
                  {isDeleteDebtorEmploymentAuthorized && (
                    <Popconfirm
                      placement="topRight"
                      okText="Yes"
                      title="Are you sure you want to delete this employment?"
                      onConfirm={deleteEmployment}
                    >
                      <Button loading={isDeleteLoading} type="link" icon={<DeleteOutlined />} />
                    </Popconfirm>
                  )}
                </>
              )}
          </StyledTrailingActions>
        </StyledCompactRow>
      )}
      {(!isCompactMode || isEditing) && (
        <StyledForm
          labelAlign="left"
          form={form}
          {...formItemLayout}
          initialValues={{ dataSource: "", companyAddressCountry: DEFAULT_COUNTRY }}
        >
          <DisplayComponent
            isEditing={isEditing}
            slice={employment}
            label="Primary"
            name="isPrimary"
            valuePropName="checked"
            hidden={employment?.isPrimary}
          >
            <Checkbox />
          </DisplayComponent>

          <DisplayComponent
            isEditing={isEditing}
            slice={employment}
            label="Company Name"
            name="companyName"
            rules={[{ required: true, message: "Name is required" }]}
          />
          <DisplayComponent
            isEditing={isEditing}
            slice={employment}
            label="Job Title"
            name="jobTitle"
          />
          <DisplayComponent isEditing={isEditing} slice={employment} label="Salary" name="salary" />
          <DisplayComponent
            isEditing={isEditing}
            slice={employment}
            label="Address Line 1"
            name="companyAddress1"
          />
          <DisplayComponent
            isEditing={isEditing}
            slice={employment}
            label="Address Line 2"
            name="companyAddress2"
          />
          <DisplayComponent
            isEditing={isEditing}
            slice={employment}
            label="Country"
            name="companyAddressCountry"
            outputFormatter={(value) => {
              const match = COUNTRIES.find((c) => c.value === value);
              return match ? match.label : value;
            }}
          >
            <Select
              popupMatchSelectWidth={false}
              allowClear
              maxTagTextLength={20}
              showSearch
              filterOption={filterOption}
              options={COUNTRIES}
              onChange={(value) => {
                setCountry(value);
                form.setFieldsValue({
                  companyAddressState: undefined,
                  companyAddressZipCode: undefined,
                });
              }}
            />
          </DisplayComponent>
          <DisplayComponent
            isEditing={isEditing}
            slice={employment}
            label="City"
            name="companyAddressCity"
            rules={[{ pattern: /^[^\d]*$/, message: "Numbers not allowed here" }]}
          />
          <DisplayComponent
            isEditing={isEditing}
            slice={employment}
            label="State/Province"
            name="companyAddressState"
          >
            {country === "CA" || country === "US" ? (
              <Select
                showSearch
                filterOption={filterOption}
                allowClear
                maxTagTextLength={20}
                popupMatchSelectWidth={false}
                options={(country === "CA" ? constants?.canadianProvinces : constants?.states)?.map(
                  (state) => ({
                    label: `${state.display} (${state.value})`,
                    value: state.value,
                  }),
                )}
              />
            ) : (
              <Input />
            )}
          </DisplayComponent>
          <DisplayComponent
            isEditing={isEditing}
            slice={employment}
            label="Zip/Postal Code"
            name="companyAddressZipCode"
            dependencies={["companyAddressCountry"]}
            placeholder={{ US: "12345", CA: "A1A 1A1" }[country] ?? ""}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const selectedCountry = getFieldValue("companyAddressCountry");
                  if (selectedCountry === "US") {
                    if (!value || /^\d{5}(-\d{4})?$/.test(value)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Must be a valid zip code"));
                  }
                  if (selectedCountry === "CA") {
                    if (!value || /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/.test(value)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Must be a valid postal code (e.g. A1A 1A1)"));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          />
          <DisplayComponent
            isEditing={isEditing}
            slice={employment}
            label="Company Phone Number"
            name="companyPhoneNumber"
            placeholder="XXX-XXX-XXXX"
          />
          <DisplayComponent
            isEditing={isEditing}
            slice={employment}
            label="Employment Start Date"
            name="startDate"
            isDate
          >
            <AktDatePicker />
          </DisplayComponent>
          <DisplayComponent
            isEditing={isEditing}
            slice={employment}
            label="Employment End Date"
            name="endDate"
            isDate
          >
            <AktDatePicker />
          </DisplayComponent>
          <DisplayComponent
            isEditing={isEditing}
            slice={employment}
            label="Employment Type"
            name="employmentType"
            outputFormatter={(value) => camelToWords(snakeToCamelCase(value))}
          >
            <Select
              showSearch
              filterOption={(input, option) =>
                (option.label.toString() ?? "").toLowerCase().includes(input.toLowerCase())
              }
              allowClear
              popupMatchSelectWidth={false}
              options={constants?.employmentTypes.map(({ display, value }) => ({
                value,
                label: display,
              }))}
            />
          </DisplayComponent>
          {isEditing && (
            <>
              <DisplayComponent isEditing={isEditing} slice={employment} label="Notes" name="notes">
                <Input.TextArea placeholder="Add Notes" />
              </DisplayComponent>
              {/* Only allow setting of data source if no data source is set, or it is editable (i.e. present in the editableDataSourceTypes) */}
              {(!employment?.dataSource ||
                editableDataSourceTypeValues.includes(employment?.dataSource)) && (
                <DisplayComponent
                  isEditing={isEditing}
                  slice={employment}
                  label="Data Source"
                  name="dataSource"
                  outputFormatter={(value) => camelToWords(snakeToCamelCase(value))}
                >
                  <Select
                    showSearch
                    filterOption={(input, option) =>
                      (option.label.toString() ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                    popupMatchSelectWidth={false}
                    options={constants?.editableDataSourceTypes?.map(({ display, value }) => ({
                      value,
                      label: display,
                    }))}
                  />
                </DisplayComponent>
              )}
              <StyledButtonContainer>
                <Space>
                  <Button onClick={isNew ? onCancel : onPrimaryCancel}>Cancel</Button>
                  {(isAddDebtorEmploymentAuthorized || isUpdateDebtorEmploymentAuthorized) && (
                    <Button
                      loading={isCreateLoading || isUpdateLoading}
                      onClick={toggleIsEditing}
                      type="primary"
                    >
                      Save
                    </Button>
                  )}
                </Space>
              </StyledButtonContainer>
            </>
          )}
          {!isEditing && (
            <>
              <DisplayComponent
                isEditing={isEditing}
                slice={employment}
                label="Data Source"
                name="dataSource"
                outputFormatter={(value) => camelToWords(snakeToCamelCase(value))}
              />
              <DisplayComponent
                isEditing={isEditing}
                slice={employment}
                label="Created At"
                name="createdAt"
                outputFormatter={() => moment(employment.createdAt).format(DATE_TIME_FORMAT)}
              />
            </>
          )}
        </StyledForm>
      )}
    </StyledCard>
  );
}
